import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RocketLaunch, CheckCircle, Circle, CaretDown, CaretUp, Robot } from "@phosphor-icons/react";
import client from "../lib/api";

const CATEGORY_ORDER = ["Business", "Payments", "Legal & Compliance", "Go Live"];

export default function AdminLaunchPage() {
  const [data, setData] = useState(null);
  const [openHow, setOpenHow] = useState(null);
  const [busy, setBusy] = useState(null);
  const [saveStats, setSaveStats] = useState(null);
  const [pushStats, setPushStats] = useState(null);
  const [featureReqs, setFeatureReqs] = useState([]);
  const [wonBack, setWonBack] = useState(null);
  const [cancels, setCancels] = useState(null);

  const load = () => client.get("/admin/launch-checklist").then((r) => setData(r.data)).catch(() => {});
  const loadFeatureReqs = () =>
    client.get("/admin/feature-requests").then((r) => {
      setFeatureReqs(r.data?.requests || []);
      setWonBack(r.data?.won_back || null);
    }).catch(() => {});
  const loadCancels = () =>
    client.get("/admin/cancel-feedback/recent").then((r) => setCancels(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadFeatureReqs();
    loadCancels();
    client.get("/admin/billing/save-offer-stats").then((r) => setSaveStats(r.data)).catch(() => {});
    client.get("/admin/billing/annual-push-stats").then((r) => setPushStats(r.data)).catch(() => {});
  }, []);

  const toggleReplied = async (c) => {
    try {
      await client.post(`/admin/cancel-feedback/${c.id}/replied`, { replied: !c.replied });
      loadCancels();
    } catch {
      toast.error("Couldn't update reply status");
    }
  };

  const setReqStatus = async (rid, status) => {
    try {
      await client.post(`/admin/feature-requests/${rid}/status`, { status });
      loadFeatureReqs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update status");
    }
  };

  const toggle = async (key) => {
    setBusy(key);
    try {
      await client.post("/admin/launch-checklist/toggle", { key });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update that step");
    } finally { setBusy(null); }
  };

  if (!data) return <div className="text-neutral-500 text-sm" data-testid="launch-loading">Loading checklist…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="launch-checklist-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight flex items-center gap-3">
          <RocketLaunch size={30} weight="fill" color="#FF5A1F" /> Launch Checklist
        </h1>
        <p className="text-neutral-400 mt-2 text-sm sm:text-base">
          Every step from LLC to your first paying customer. Tap a step to mark it done — a few check themselves.
        </p>
      </div>

      <div className="card-surface p-5" data-testid="launch-progress-card">
        <div className="flex items-center justify-between mb-2">
          <span className="overline">PROGRESS</span>
          <span className="text-sm font-mono font-bold text-[#FF9764]" data-testid="launch-progress-pct">
            {data.done}/{data.total} · {data.pct}%
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FF5A1F] to-[#FF9764] transition-all duration-500"
               style={{ width: `${data.pct}%` }} />
        </div>
      </div>

      {saveStats && (
        <div className="card-surface p-5" data-testid="launch-save-offer-card">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">SAVE OFFER — IS IT PAYING FOR ITSELF?</span>
            <span className="text-sm font-mono font-bold text-emerald-400" data-testid="save-offer-rate">
              {saveStats.save_rate_pct}% saved
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-red-500/20 bg-red-500/[0.05] py-3">
              <div className="text-2xl font-bold text-red-400" data-testid="save-offer-cancels">{saveStats.cancels}</div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">cancels asked</div>
            </div>
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] py-3">
              <div className="text-2xl font-bold text-emerald-400" data-testid="save-offer-saves">{saveStats.saves}</div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">took 20% offer</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] py-3">
              <div className="text-2xl font-bold text-[#FF9764]" data-testid="save-offer-answered">{saveStats.answered}</div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-0.5">told you why</div>
            </div>
          </div>
          {saveStats.reasons?.length > 0 ? (
            <>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="save-offer-reasons">
                {saveStats.reasons.map((r) => (
                  <span key={r.label} title={r.playbook}
                        className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[11px] text-neutral-300">
                    {r.label} <b className="text-red-400">×{r.count}</b>
                  </span>
                ))}
              </div>
              {saveStats.reasons[0]?.playbook && (
                <p className="mt-2.5 rounded-lg bg-black/30 border border-[#FF5A1F]/20 px-3 py-2.5 text-[12px] text-neutral-300 leading-relaxed"
                   data-testid="save-offer-playbook">
                  <b className="text-[#FF9764]">📖 Playbook for "{saveStats.reasons[0].label}":</b>{" "}
                  {saveStats.reasons[0].playbook}
                </p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-neutral-500 mt-3">
              No cancels yet — the 20% save offer shows automatically on every cancel-feedback page.
            </p>
          )}
          {saveStats.variants?.some((v) => v.asked > 0) && (() => {
            const [v20, v30] = [saveStats.variants.find((v) => v.pct === 20), saveStats.variants.find((v) => v.pct === 30)];
            const bothLive = v20?.asked > 0 && v30?.asked > 0;
            const leader = bothLive && v20.save_rate_pct !== v30.save_rate_pct
              ? (v20.save_rate_pct > v30.save_rate_pct ? 20 : 30) : null;
            return (
              <div className="mt-3" data-testid="save-offer-variants">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">20% vs 30% — which saves more?</div>                <div className="grid grid-cols-2 gap-3">
                  {[v20, v30].filter(Boolean).map((v) => (
                    <div key={v.pct}
                         data-testid={`save-offer-variant-${v.pct}`}
                         className={`rounded-xl border py-3 px-3 text-center ${
                           leader === v.pct ? "border-emerald-400/50 ring-1 ring-emerald-400/25 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02]"
                         }`}>
                      <div className="text-[11px] font-bold text-neutral-400">
                        {v.pct}% offer {leader === v.pct && <span className="text-emerald-400">· winning</span>}
                      </div>
                      <div className="text-xl font-bold text-white mt-0.5">{v.save_rate_pct}%</div>
                      <div className="text-[10px] text-neutral-500">{v.saves} saved of {v.asked} asked</div>
                    </div>
                  ))}
                </div>
                {saveStats.auto_applied_pct && (
                  <p className="mt-2 text-[11px] font-bold text-emerald-400" data-testid="save-offer-auto-applied">
                    🏁 Test decided — all new cancels now get the {saveStats.auto_applied_pct}% offer automatically (both variants hit 10+ asks).
                  </p>
                )}
              </div>
            );
          })()}
          {pushStats && (
            <p className="mt-3 text-[11px] text-neutral-500 border-t border-white/5 pt-2.5" data-testid="annual-push-stats">
              📧 Annual push ("2 months free" after month 3):{" "}
              <b className="text-neutral-300">{pushStats.sent} sent</b> ·{" "}
              <b className="text-emerald-400">{pushStats.switched} switched</b>
              {pushStats.sent > 0 && <> · <b className="text-[#FF9764]">{pushStats.switch_rate_pct}%</b></>}
            </p>
          )}
        </div>
      )}

      {featureReqs.length > 0 && (
        <div className="card-surface p-5" data-testid="feature-request-card">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">FEATURE REQUESTS — THE ROADMAP BUILDS ITSELF</span>
            <span className="text-sm font-mono font-bold text-sky-400" data-testid="feature-request-count">
              {featureReqs.filter((r) => r.status === "new").length} new
              {wonBack?.count > 0 && (
                <span className="ml-2 text-emerald-400" data-testid="feature-request-wonback">
                  · {wonBack.count} won back 💚{wonBack.monthly_revenue > 0 ? ` $${wonBack.monthly_revenue.toLocaleString()}/mo` : ""}
                </span>
              )}
            </span>
          </div>
          <div className="space-y-2">
            {featureReqs.map((r) => (
              <div key={r.id} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
                   data-testid={`feature-request-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">
                      "{r.text}"
                      {(r.votes || 1) > 1 && (
                        <span className="ml-2 rounded-full bg-sky-400/15 border border-sky-400/40 px-2 py-0.5 text-[10px] font-bold text-sky-300"
                              data-testid={`feature-request-votes-${r.id}`}>
                          🗳 {r.votes} votes
                        </span>
                      )}
                      {(r.comebacks?.length || 0) > 0 && (
                        <span className="ml-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-2 py-0.5 text-[10px] font-bold text-emerald-300"
                              data-testid={`feature-request-comebacks-${r.id}`}>
                          💚 {r.comebacks.length} back
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {r.email}{r.company ? ` · ${r.company}` : ""} · {new Date(r.created_at).toLocaleDateString()} · {r.source === "ai_chat" ? "asked in AI chat" : "canceled over this"}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {["new", "planned", "shipped"].map((s) => (
                      <button key={s} type="button" onClick={() => setReqStatus(r.id, s)}
                              data-testid={`feature-request-status-${r.id}-${s}`}
                              className={`rounded-full px-2.5 py-1 text-[10px] font-bold transition-colors ${
                                r.status === s
                                  ? s === "shipped" ? "bg-emerald-500 text-black" : s === "planned" ? "bg-sky-500 text-black" : "bg-white/20 text-white"
                                  : "bg-white/5 text-neutral-400 hover:text-white"
                              }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {cancels?.cancels?.length > 0 && (
        <div className="card-surface p-5" data-testid="cancel-reply-log-card">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">RECENT CANCELS — DID YOU REPLY?</span>
            <span className={`text-sm font-mono font-bold ${cancels.unreplied > 0 ? "text-red-400" : "text-emerald-400"}`}
                  data-testid="cancel-unreplied-count">
              {cancels.unreplied > 0 ? `${cancels.unreplied} waiting` : "all handled ✓"}
            </span>
          </div>
          <div className="space-y-1.5">
            {cancels.cancels.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2"
                   data-testid={`cancel-row-${c.id}`}>
                <div className="min-w-0 text-[12px]">
                  <span className="font-semibold text-white">{c.email}</span>
                  <span className="text-neutral-500"> · {c.reason} · {c.answered_at ? new Date(c.answered_at).toLocaleDateString() : "—"}</span>
                  {c.saved && <span className="ml-1.5 text-emerald-400 font-bold">💚 saved</span>}
                </div>
                <button type="button" onClick={() => toggleReplied(c)}
                        data-testid={`cancel-replied-toggle-${c.id}`}
                        className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold transition-colors ${
                          c.replied
                            ? "bg-emerald-500 text-black"
                            : "bg-white/5 text-neutral-400 border border-white/15 hover:text-white"
                        }`}>
                  {c.replied ? "Replied ✓" : "Mark replied"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {CATEGORY_ORDER.map((cat) => {
        const steps = data.steps.filter((s) => s.category === cat);
        if (!steps.length) return null;
        return (
          <div key={cat} className="card-surface p-5" data-testid={`launch-category-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="overline mb-3">{cat.toUpperCase()}</div>
            <div className="space-y-2">
              {steps.map((s) => (
                <div key={s.key} className={`rounded-xl border px-4 py-3 ${s.done ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => !s.auto && toggle(s.key)}
                      disabled={s.auto || busy === s.key}
                      data-testid={`launch-step-toggle-${s.key}`}
                      className={`mt-0.5 shrink-0 ${s.auto ? "cursor-default" : "hover:scale-110 transition-transform"}`}
                      title={s.auto ? "Checks itself automatically" : s.done ? "Mark as not done" : "Mark as done"}
                    >
                      {s.done
                        ? <CheckCircle size={22} weight="fill" color="#34d399" />
                        : <Circle size={22} color="#666" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${s.done ? "text-emerald-300 line-through decoration-emerald-500/40" : "text-white"}`}>
                          {s.title}
                        </span>
                        {s.auto && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[9px] font-bold text-neutral-400"
                                title="Checks itself automatically">
                            <Robot size={10} /> AUTO
                          </span>
                        )}
                      </div>
                      <p className="text-[12px] text-neutral-400 mt-0.5 leading-relaxed">{s.desc}</p>
                      {s.how && (
                        <button
                          type="button"
                          onClick={() => setOpenHow(openHow === s.key ? null : s.key)}
                          data-testid={`launch-step-how-${s.key}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#FF9764] hover:text-[#FF7038] transition-colors"
                        >
                          How to do it {openHow === s.key ? <CaretUp size={11} /> : <CaretDown size={11} />}
                        </button>
                      )}
                      {s.how && openHow === s.key && (
                        <p className="mt-2 rounded-lg bg-black/30 border border-white/5 px-3 py-2.5 text-[12px] text-neutral-300 leading-relaxed"
                           data-testid={`launch-step-howtext-${s.key}`}>
                          {s.how}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <p className="text-[11px] text-neutral-500">
        Rough cost to go official: ~$150 LLC + $0 EIN + free business bank = under $200 one-time.
        Stripe takes 2.9% + 30¢ per payment — no monthly fee.
      </p>
    </div>
  );
}
